import { IconButton, TableColumn, Typography } from '@app/components';
import { NestedKeyOf } from '@app/utils/type.utils';
import { useMemo, useState } from 'react';
import { TeamMemberOutDtoSchema } from '../api/team.schema';
import { humanizeEnum } from '@app/utils/string.utils';
import { DeleteIcon } from '@app/assets';
import { ETeamMemberRole, ETeamMemberStatus } from '@app/swagger-types';
import { clsxm } from '@app/styles/clsxm';
import { useDeleteTeamMember } from '../api/team.queries';
import { useHandler } from '@app/hooks/useHandler.hook';
import { ConfirmationRemoveMemberDialog } from './AccountTeamManagement';
import { useModal } from '@ebay/nice-modal-react';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useCurrentTeam } from '../hooks/useCurrentTeam';
import { useAuthorizedUser } from '@app/auth/useAuthorizedUser';

const getEntityField = (v: NestedKeyOf<TeamMemberOutDtoSchema>) => v;

export const useTableColumns = () => {
  const { t } = useTranslation();
  const { teamId } = useCurrentTeam();
  const { currentUser } = useAuthorizedUser();
  const isCurrentUserOwner = currentUser?.teamMemberRole === ETeamMemberRole.OWNER;

  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const { mutate: deleteMemberMutation } = useDeleteTeamMember();
  const { show: openDialog } = useModal(ConfirmationRemoveMemberDialog);

  const deleteMemberHandler = useHandler(async (teamId: string, teamMemberId: string) => {
    setDeletingMemberId(teamMemberId);

    await deleteMemberMutation(
      { teamId, teamMemberId },
      {
        onSuccess: () => {
          toast.success(t('toaster.memberRemoved'));
        },
        onSettled: () => {
          setDeletingMemberId(null);
        },
      }
    );
  });

  const handlerOpenDialog = useHandler((id: string, memberMail: string) => {
    openDialog({ onConfirm: () => teamId && deleteMemberHandler(teamId, id), memberMail });
  });

  const tableColumns = useMemo<TableColumn<TeamMemberOutDtoSchema>[]>(() => {
    return [
      {
        headerName: 'Email',
        field: getEntityField('user.email'),
        align: 'left',
        render: ({ user: { email } }) => (
          <Typography variant="xs" className="pl-3">
            {email || 'unknown.user@mail.com'}
          </Typography>
        ),
      },
      {
        headerName: 'Status',
        field: getEntityField('status'),
        align: 'left',
        render: ({ status }) => {
          return <TeamMemberStatusBadge name={humanizeEnum(status)} status={status} />;
        },
      },
      {
        headerName: 'Role',
        field: getEntityField('role'),
        align: 'left',
        render: ({ role, id, status, user }) => {
          const isDeleting = deletingMemberId === id;
          const isDeleteDisabled =
            status === ETeamMemberStatus.DELETED || isDeleting || role === ETeamMemberRole.OWNER || !isCurrentUserOwner;

          return (
            <div className="flex items-center justify-between pr-3">
              <span>{humanizeEnum(role)}</span>
              <IconButton
                disabled={isDeleteDisabled}
                onClick={() => handlerOpenDialog(id, user.email)}
                variant="transparent"
                className="disabled:opacity-30 disabled:text-gray-200"
              >
                {isDeleting ? (
                  <CircularProgress size={24} className="inline w-6 h-6" />
                ) : (
                  <DeleteIcon
                    role="button"
                    className="cursor-pointer transition text-gray-800 hover:text-accept-hover"
                  />
                )}
              </IconButton>
            </div>
          );
        },
      },
    ];
  }, [handlerOpenDialog, deletingMemberId, isCurrentUserOwner]);

  return tableColumns;
};

export const TeamMemberStatusBadge = ({ name, status }: { name: string; status: ETeamMemberStatus }) => {
  let badgeClass = '';

  switch (status) {
    case ETeamMemberStatus.PENDING:
      badgeClass = 'bg-gray-200 text-gray-700';
      break;
    case ETeamMemberStatus.ACTIVE:
      badgeClass = 'bg-accept-light text-accept-main';
      break;
    case ETeamMemberStatus.DELETED:
      badgeClass = 'bg-red-200 text-red-700';
      break;
    default:
      const _exhaustiveCheck: never = status;
      throw new Error(`Unhandled status: ${_exhaustiveCheck}`);
  }

  return <span className={clsxm('rounded-lg p-1 px-4', badgeClass)}>{name}</span>;
};
